import MDBox from "components/MDBox";
import { useAppServices } from "hook/services";
import { useAuth } from "hook/auth";
import env from "config";
import axios from "axios";
import { useEffect, useState, useRef } from "react";
import EditModal from "../../EditModal";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";


export const BOOKING_STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "Pending", label: "Pending" },
  { value: "To be paid", label: "To be paid" },
  { value: "Paid", label: "Paid" },
  { value: "Declined", label: "Declined" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Completed", label: "Completed" },
  { value: "Expired", label: "Expired" },
];

export default function useAppData({ setChangeReqBooking } = {}) {
  const AppService = useAppServices();
  const token = useAuth();
  const [list, setList] = useState([]);
  const [loader, setLoader] = useState(true);
  const [search, setSearch] = useState("");
  const typingTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const [cancelReason, setCancelReason] = useState("");
  const [excludeTesting, setExcludeTesting] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [discountBooking, setDiscountBooking] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    total: 0,
  });

  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const getBookings = async (
    page = 1,
    limit = 10,
    searchTerm = search,
    excludeTestingValue = excludeTesting,
    statusValue = statusFilter
  ) => {
    setLoader(true);
    try {
      const params = {
        page,
        limit,
        search: searchTerm?.trim() || "",
        startDate: dateRange.startDate ? dateRange.startDate.toISOString() : "null",
        endDate: dateRange.endDate ? dateRange.endDate.toISOString() : "null",
        excludeTesting: excludeTestingValue,
        status: statusValue || "all",
      };
      console.log("API excludeTesting:", params.excludeTesting);

      const query = Object.entries(params)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&");

      const { response } = await AppService.booking.get({ query });
      let bookingsList = response.data || [];
      if (response?.success) {
        setList(bookingsList || []);
        setPagination({
          page: response.pagination?.page || page,
          limit: response.pagination?.limit || limit,
          totalPages: response.pagination?.totalPages || 0,
          total: response.pagination?.total || 0,
          total: bookingsList.length,
        });
      } else {
        setList([]);
      }
    } catch (err) {
      console.error("❌ Error fetching bookings:", err);
      setList([]);
    } finally {
      setLoader(false);
    }
  };

  console.log("excludeTesting", excludeTesting);

  const handleRetryWorkflow = async (bookingId) => {
    if (!bookingId) return;
    try {
      const res = await AppService.booking.runBookingWorkflowByBookingId({
        payload: { bookingId },
      });
      if (res?.response?.success) {
        toast.success("Workflow retried successfully");
      } else {
        toast.error(res?.response?.message || "Retry failed");
      }
    } catch (error) {
      console.error("❌ Retry workflow error:", error);
      toast.error("Something went wrong while retrying workflow");
    }
  };

  const onLoad = (excludeTestingValue = excludeTesting) => {
    getBookings(pagination.page, pagination.limit, search, excludeTestingValue);
  };

  useEffect(() => {
    getBookings(1, pagination.limit);
  }, [dateRange]);

  useEffect(() => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      getBookings(1, pagination.limit, search);
    }, 800);
    return () => clearTimeout(typingTimeoutRef.current);
  }, [search]);

  /* ================= HELPERS ================= */
  const handlePageChange = (newPage) => {
    getBookings(newPage, pagination.limit, search);
  };

  const handleLimitChange = (newLimit) => {
    getBookings(1, newLimit, search);
  };

  // Status filter — reset to page 1 and refetch with the chosen status so the
  // table (and its pagination) reflects only that status.
  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    getBookings(1, pagination.limit, search, excludeTesting, value);
  };

  const exportBookings = async () => {
    if (exporting) return;
    try {
      setExporting(true);
      const params = {
        status: statusFilter || "all",
        excludeTesting,
      };
      const query = Object.entries(params)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&");

      const res = await axios.get(
        `${env.API_URL}/v1/bookings/export?${query}`,
        {
          responseType: "blob",
          headers: { "x-auth-token": `Bearer ${token}` },
        }
      );

      const blob = new Blob([res.data], {
        type:
          res.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const label =
        statusFilter && statusFilter !== "all"
          ? "-" + statusFilter.replace(/\s+/g, "")
          : "";
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `bookings${label}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
      toast.success("Bookings exported to Excel");
    } catch (err) {
      console.error("❌ Export bookings error:", err);
      toast.error("Failed to export bookings");
    } finally {
      setExporting(false);
    }
  };

  const handleUpdateStatus = async (booking, status, updatedBy) => {
    if (!booking) return;
    try {
      const payload = { ...booking, status, updatedBy, cancelReason };
      const res = await AppService.booking.update({ payload });
      if (res?.response?.success) {
        toast.success(`Booking ${status.toLowerCase()} successfully`);
        getBookings(pagination.page, pagination.limit);
        setCancelReason("");
      } else {
        toast.error("Failed to update booking");
      }
    } catch (err) {
      console.error("❌ Update booking error:", err);
      toast.error("Something went wrong");
    }
  };

  const handleManualPayment = async (booking) => {
    if (!booking) return;
    try {
      const payload = { booking };
      const res = await AppService.booking.manualPayment({ payload });
      if (res?.response?.success) {
        toast.success(`Booking ${booking.bookingNo} marked as Paid`);
        getBookings(pagination.page, pagination.limit);
      } else {
        toast.error("Failed to mark booking as Paid");
      }
    } catch (err) {
      console.error("Manual Payment error:", err);
      toast.error("Something went wrong while marking payment");
    }
  };

  const truncateText = (text, wordLimit = 6) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.length > wordLimit
      ? words.slice(0, wordLimit).join(" ") + "..."
      : text;
  };

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-GB") : "";

  const renderHostTransfer = (ht) => {
    if (!ht || !ht.status) {
      return <span style={{ color: "#9CA3AF", fontSize: 12 }}>—</span>;
    }
    const amountAud = ht.amount ? (ht.amount / 100).toFixed(2) : null;
    const date = ht.transferredAt || ht.blockedAt || ht.claimedAt;
    const dateStr = date ? new Date(date).toLocaleString("en-GB") : "";

    const styles = {
      succeeded: { bg: "#D1FAE5", fg: "#065F46", label: `Paid A$${amountAud}` },
      pending: { bg: "#FEF3C7", fg: "#92400E", label: "Pending" },
      failed: { bg: "#FEE2E2", fg: "#991B1B", label: "Failed" },
      blocked_no_connect_account: {
        bg: "#FEE2E2", fg: "#991B1B", label: "Blocked · No Connect",
      },
    };
    const s = styles[ht.status] || { bg: "#E5E7EB", fg: "#374151", label: ht.status };

    const tooltip = [
      ht.transferId && `Transfer ID: ${ht.transferId}`,
      amountAud && `Amount: A$${amountAud}`,
      dateStr && `When: ${dateStr}`,
      ht.blockReason && `Reason: ${ht.blockReason}`,
      ht.lastError && `Error: ${ht.lastError}`,
    ].filter(Boolean).join("\n");

    return (
      <span
        title={tooltip}
        style={{
          background: s.bg, color: s.fg,
          padding: "4px 8px", borderRadius: 6,
          fontSize: 12, fontWeight: 600,
          whiteSpace: "nowrap", cursor: "help",
        }}
      >
        {s.label}
      </span>
    );
  };

  return {
    loader,
    handleRefresh: onLoad,
    setDateRange,
    dateRange,
    pagination,
    handlePageChange,
    handleLimitChange,
    search,
    setSearch,
    cancelReason,
    setCancelReason,
    excludeTesting,
    setExcludeTesting,
    statusFilter,
    handleStatusFilter,
    statusOptions: BOOKING_STATUS_OPTIONS,
    exportBookings,
    exporting,
    discountBooking,
    setDiscountBooking,

    dataTableData: {
      columns: [
        { Header: "Booking No", accessor: "bookingNo" },
        { Header: "Date", accessor: "createdAt" },
        { Header: "Company Name", accessor: "company" },
        { Header: "Booking Date", accessor: "bookingDate" },
        { Header: "Duration", accessor: "duration" },
        { Header: "Listing", accessor: "listing" },
        { Header: "Total", accessor: "total" },
        { Header: "Status", accessor: "status" },
        { Header: "Host Payout", accessor: "hostPayout" },
        // { Header: "Change request", accessor: "isChangeRequestPending" },
        // { Header: "Change Request Status", accessor: "isChangeRequestStatus" },
        { Header: "Action", accessor: "action" },
      ],

      rows: list.map((data) => ({
        bookingNo: data?.bookingNo || "-",
        createdAt: formatDate(data?.createdAt),
        company: data?.company || "-",
        bookingDate: formatDate(data?.shootDetail?.shootDate),
        duration: data?.shootDetail?.duration || "-",
        listing: truncateText(data?.shootDetail?.listing?.title, 4),
        total: `$${data?.bookingPrice || 0}`,
        status: data?.status || "Pending",
        hostPayout: renderHostTransfer(data?.hostTransfer),
        // isChangeRequestPending: data?.isChangeRequestPending ? "Yes" : "No",
        // isChangeRequestStatus: data?.isChangeRequestStatus || null,

        action: (
          <MDBox display="flex" gap={1}>

            {!data.workflowRan && (
              <button
                style={{
                  background: "blue", color: "white",
                  padding: "6px 12px", borderRadius: "6px",
                  border: "none", cursor: "pointer",
                }}
                onClick={() => {
                  if (!window.confirm("Are you sure you want to retry workflow?")) return;
                  handleRetryWorkflow(data?._id);
                }}
              >
                Pending Retry Workflow
              </button>
            )}

            {/* {data?.isChangeRequestPending && (
              <button
                style={{
                  background: "#F59E0B", color: "white",
                  padding: "6px 12px", borderRadius: "6px",
                  border: "none", cursor: "pointer",
                  fontWeight: 600,
                }}
                onClick={() => navigate(`/agency/Bookings/change-request/${data.bookingNo}`)}

              >
                View New Request
              </button>
            )} */}

            <button
              style={{
                background: "blue", color: "white",
                padding: "6px 12px", borderRadius: "6px",
                border: "none", cursor: "pointer",
              }}
              onClick={() => {
                if (!window.confirm("Are you sure you want to cancel this booking?")) return;
                const hostId = data?.shootDetail?.listing?.user_id?._id;
                handleUpdateStatus(data, "Declined", hostId);
              }}
            >
              Host Declined
            </button>

            <button
              style={{
                background: "blue", color: "white",
                padding: "6px 12px", borderRadius: "6px",
                border: "none", cursor: "pointer",
              }}
              onClick={() => {
                if (!window.confirm("Are you sure you want to decline this booking?")) return;
                const creativeId = data?.shootDetail?.bookedBy?._id;
                handleUpdateStatus(data, "Cancelled", creativeId);
              }}
            >
              Creative Cancel
            </button>

            {data.status === "To be paid" && (
              <button
                style={{
                  background: "green", color: "white",
                  padding: "6px 12px", borderRadius: "6px",
                  border: "none", cursor: "pointer",
                }}
                onClick={() => {
                  if (!window.confirm("Are you sure you want to mark this booking as Paid?")) return;
                  handleManualPayment(data);
                }}
              >
                Mark as Paid
              </button>
            )}

            {(data.status === "Pending" || data.status === "To be paid") && (
              <button
                style={{
                  background: "#7B1FA2", color: "white",
                  padding: "6px 12px", borderRadius: "6px",
                  border: "none", cursor: "pointer",
                  fontWeight: 600,
                }}
                onClick={() => setDiscountBooking(data)}
              >
                Add Discount
              </button>
            )}

          </MDBox>
        ),
      })),
    },
  };
}